
import React from 'react';
import { motion } from 'framer-motion';
import { X, MapPin, Camera, Utensils, Compass, Star, Clock, Sparkles, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';

const destinationData = {
  "Santorini, Greece": {
    hero: "https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?q=80&w=2070&auto=format&fit=crop",
    description: "A breathtaking Greek island where whitewashed villages cascade down volcanic cliffs, creating one of the world's most iconic sunsets.",
    highlights: [
      { name: "Oia Village", description: "Famous for stunning sunsets and blue-domed churches", icon: Camera },
      { name: "Red Beach", description: "Unique volcanic beach with dramatic red cliffs", icon: MapPin },
      { name: "Wine Tasting", description: "Ancient vineyards producing world-class Assyrtiko", icon: Utensils },
      { name: "Caldera Views", description: "Panoramic views of the volcanic crater", icon: Compass }
    ],
    experiences: [
      "Sunset dinner in Oia with panoramic caldera views",
      "Private wine tour through traditional cave wineries",
      "Catamaran cruise around the volcanic islands",
      "Explore ancient Akrotiri archaeological site",
      "Luxury spa treatments overlooking the Aegean Sea"
    ],
    bestTime: "April to October",
    duration: "4-7 days",
    vibe: ["Romantic", "Luxury", "Photography"]
  },
  "Kyoto, Japan": {
    hero: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?q=80&w=2070&auto=format&fit=crop",
    description: "Japan's ancient capital where over 2,000 temples coexist with modern culture, offering a perfect blend of tradition and innovation.",
    highlights: [
      { name: "Fushimi Inari Shrine", description: "10,000 vermillion torii gates winding up the mountain", icon: Compass },
      { name: "Bamboo Grove", description: "Ethereal bamboo forest creating natural light tunnels", icon: MapPin },
      { name: "Kaiseki Dining", description: "Multi-course culinary art form in traditional settings", icon: Utensils },
      { name: "Geisha District", description: "Historic Gion district with traditional tea houses", icon: Camera }
    ],
    experiences: [
      "Private tea ceremony with a tea master in historic temple",
      "Early morning meditation session with Buddhist monks",
      "Cherry blossom viewing in Philosopher's Path",
      "Traditional ryokan stay with onsen hot springs",
      "Sake brewing tour in historic Fushimi district"
    ],
    bestTime: "March to May, September to November",
    duration: "5-8 days",
    vibe: ["Cultural", "Spiritual", "Culinary"]
  },
  "Swiss Alps": {
    hero: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=2070&auto=format&fit=crop",
    description: "Majestic mountain peaks, pristine lakes, and charming alpine villages create the perfect backdrop for luxury mountain adventures.",
    highlights: [
      { name: "Jungfraujoch", description: "Top of Europe with glaciers and snow year-round", icon: Compass },
      { name: "Lake Geneva", description: "Crystal clear lake surrounded by vineyards", icon: MapPin },
      { name: "Alpine Cuisine", description: "Fondue, raclette, and fine mountain dining", icon: Utensils },
      { name: "Matterhorn", description: "Iconic pyramid-shaped peak in Zermatt", icon: Camera }
    ],
    experiences: [
      "Scenic train journey on the Glacier Express",
      "Helicopter tour over snow-capped peaks and glaciers",
      "Luxury spa retreat in alpine wellness centers",
      "Private skiing lessons on world-class slopes",
      "Mountain hiking with professional alpine guides"
    ],
    bestTime: "December to April (skiing), June to September (hiking)",
    duration: "6-10 days",
    vibe: ["Adventure", "Luxury", "Nature"]
  },
  "Maldives": {
    hero: "https://images.unsplash.com/photo-1573843981267-be1999ff37cd?q=80&w=2070&auto=format&fit=crop",
    description: "An archipelago of 1,192 coral islands where crystal-clear lagoons meet pristine white beaches, creating the ultimate tropical paradise.",
    highlights: [
      { name: "Overwater Villas", description: "Luxury accommodations suspended over turquoise lagoons", icon: Camera },
      { name: "Coral Reef Diving", description: "World-class underwater biodiversity with manta rays", icon: Compass },
      { name: "Underwater Restaurant", description: "Dine surrounded by marine life in glass-walled venues", icon: Utensils },
      { name: "Private Beach Access", description: "Secluded beaches with powder-soft white sand", icon: MapPin }
    ],
    experiences: [
      "Sunset dolphin cruise with champagne service",
      "Private island picnic on an uninhabited atoll", 
      "Submarine exploration of deep coral gardens",
      "Couples spa treatment in overwater pavilions",
      "Traditional Maldivian fishing with local guides"
    ],
    bestTime: "November to April",
    duration: "5-10 days",
    vibe: ["Romantic", "Luxury", "Relaxation"]
  },
  "Dubai, UAE": {
    hero: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=2070&auto=format&fit=crop",
    description: "A futuristic metropolis where architectural marvels rise from golden deserts, offering unparalleled luxury and innovation.",
    highlights: [
      { name: "Burj Khalifa", description: "World's tallest building with observation decks", icon: Camera },
      { name: "Desert Safari", description: "Luxury camping under the stars in golden dunes", icon: Compass },
      { name: "Gold Souk", description: "Traditional markets with world's finest jewelry", icon: MapPin },
      { name: "Fine Dining", description: "Michelin-starred restaurants with skyline views", icon: Utensils }
    ],
    experiences: [
      "Private helicopter tour over Palm Jumeirah",
      "Luxury yacht charter along Dubai Marina",
      "VIP shopping experience at Dubai Mall",
      "Traditional hammam spa treatments",
      "Exclusive access to Atlantis underwater suites"
    ],
    bestTime: "November to March",
    duration: "4-6 days", 
    vibe: ["Luxury", "Modern", "Shopping"]
  },
  "Bali, Indonesia": {
    hero: "https://images.unsplash.com/photo-1537953773345-d172ccf13cf1?q=80&w=2070&auto=format&fit=crop",
    description: "The Island of the Gods where ancient temples blend with lush rice terraces, creating a spiritual haven for mind, body, and soul.",
    highlights: [
      { name: "Ubud Rice Terraces", description: "UNESCO World Heritage cascading green landscapes", icon: MapPin },
      { name: "Temple Ceremonies", description: "Sacred Hindu rituals at ancient temple complexes", icon: Compass },
      { name: "Balinese Cuisine", description: "Authentic flavors with traditional cooking classes", icon: Utensils },
      { name: "Volcanic Sunrise", description: "Mount Batur sunrise trekking experience", icon: Camera }
    ],
    experiences: [
      "Private villa retreat with infinity pool views",
      "Traditional Balinese massage and wellness treatments",
      "Sunrise yoga sessions overlooking rice paddies",
      "Cultural immersion with local artisan workshops", 
      "Sacred water temple purification ceremonies"
    ],
    bestTime: "April to September",
    duration: "7-10 days",
    vibe: ["Spiritual", "Cultural", "Wellness"]
  },
  "Paris, France": {
    hero: "https://images.unsplash.com/photo-1431274172761-fca41d930114?q=80&w=2070&auto=format&fit=crop",
    description: "The City of Light where artistic masterpieces, culinary excellence, and timeless romance create an unforgettable urban symphony.",
    highlights: [
      { name: "Eiffel Tower", description: "Iconic iron lattice tower with panoramic city views", icon: Camera },
      { name: "Louvre Museum", description: "World's largest art museum with priceless collections", icon: Compass },
      { name: "Seine River Cruise", description: "Romantic boat tours past historic landmarks", icon: MapPin },
      { name: "Michelin Dining", description: "World-renowned culinary experiences", icon: Utensils }
    ],
    experiences: [
      "Private after-hours Louvre museum tour",
      "Champagne tasting in historic Parisian caves", 
      "Exclusive fashion shopping with personal stylist",
      "Sunset picnic in Versailles palace gardens",
      "Private cooking class with renowned French chefs"
    ],
    bestTime: "April to June, September to October",
    duration: "4-8 days",
    vibe: ["Romantic", "Cultural", "Culinary"]
  },
  "Iceland": {
    hero: "https://images.unsplash.com/photo-1551524164-687a55dd1126?q=80&w=2070&auto=format&fit=crop",
    description: "A Nordic wonderland where fire meets ice, creating dramatic landscapes of glaciers, geysers, and the mesmerizing Northern Lights.",
    highlights: [
      { name: "Northern Lights", description: "Aurora borealis viewing in pristine dark skies", icon: Camera },
      { name: "Blue Lagoon", description: "Geothermal spa with mineral-rich healing waters", icon: Compass },
      { name: "Glacier Hiking", description: "Guided exploration of ancient ice formations", icon: MapPin },
      { name: "Nordic Cuisine", description: "Fresh seafood and traditional Icelandic delicacies", icon: Utensils }
    ],
    experiences: [
      "Private Northern Lights photography tours",
      "Helicopter flights over volcanic landscapes",
      "Ice cave exploration with expert guides",
      "Luxury glacier lodge accommodations",
      "Whale watching from Reykjavik harbor"
    ],
    bestTime: "September to March (Northern Lights), June to August (Hiking)",
    duration: "5-8 days",
    vibe: ["Adventure", "Nature", "Photography"]
  }
};

export default function DestinationModal({ destination, isOpen, onClose }) {
  if (!isOpen || !destination) return null;

  const data = destinationData[destination.name];
  if (!data) return null;

  const listVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ paddingTop: '60px', paddingBottom: '60px' }}
    >
      <motion.div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />

      <motion.div
        className="relative glass rounded-2xl max-w-5xl w-full max-h-full overflow-y-auto"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with Hero Image */}
        <div className="relative h-56 sm:h-72 overflow-hidden rounded-t-2xl">
          <img
            src={data.hero}
            alt={destination.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute top-4 right-4 z-10">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white bg-black/30 hover:bg-black/50 rounded-full"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          <div className="absolute bottom-6 left-6">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-2 shadow-2xl">{destination.name}</h2>
          </div>
        </div>

        {/* Content Body */}
        <div className="p-6 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <motion.p
              className="text-white/90 leading-relaxed text-base"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              {data.description}
            </motion.p>

            {/* Top Sights */}
            <div>
              <h2 className="text-xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400">Top Sights</h2>
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                variants={listVariants}
                initial="hidden"
                animate="visible"
              >
                {data.highlights.map((highlight, index) => {
                  const IconComponent = highlight.icon;
                  return (
                    <motion.div
                      key={index}
                      className="glass-light rounded-xl p-4 transition-all duration-300 hover:bg-[var(--glass-light-bg)] hover:shadow-lg hover:-translate-y-1 hover:border-white/20"
                      variants={itemVariants}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 glass rounded-lg flex items-center justify-center flex-shrink-0 bg-primary/20 shadow-inner">
                          <IconComponent className="w-5 h-5 text-primary-foreground" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-white mb-1">{highlight.name}</h4>
                          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{highlight.description}</p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </div>

            {/* Curated Experiences */}
            <div>
              <h2 className="text-xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400">Unforgettable Experiences</h2>
              <motion.ul
                className="space-y-3"
                variants={listVariants}
                initial="hidden"
                animate="visible"
              >
                {data.experiences.map((experience, index) => (
                  <motion.li
                    key={index}
                    className="glass-light rounded-lg p-3 flex items-center gap-4"
                    variants={itemVariants}
                  >
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <p className="text-sm text-white/90">{experience}</p>
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="glass-light rounded-xl p-5 space-y-5">
              <h3 className="font-bold text-lg text-white">At a Glance</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-1 text-sm text-gray-300">
                    <Clock className="w-4 h-4" />
                    <span>Best Time to Visit</span>
                  </div>
                  <p className="text-base font-medium text-white">{data.bestTime}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1 text-sm text-gray-300">
                    <Star className="w-4 h-4" />
                    <span>Recommended Duration</span>
                  </div>
                  <p className="text-base font-medium text-white">{data.duration}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2 text-sm text-gray-300">
                    <Sparkles className="w-4 h-4" />
                    <span>Travel Vibe</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {data.vibe.map((tag, index) => (
                      <Badge key={index} className="bg-primary/40 text-primary-foreground border-primary/20 text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-light rounded-xl p-6 text-center hover-glow">
              <h3 className="text-lg font-bold text-white mb-2">Ready to Go?</h3>
              <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                Let's turn this inspiration into your next great journey.
              </p>
              <Link to={createPageUrl('search')}>
                <Button className="w-full bg-[var(--accent-color)] hover:bg-primary/90 text-white font-semibold px-6 py-3 h-auto shadow-lg shadow-primary/30">
                  Plan Your Journey
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
